import BackButton from '@/components/ui/BackButton'
import FormField from '@/components/ui/FormField'
import { canUpdateGPs } from '@/lib/permissions'
import { useUnsavedChangesProcessing } from '@/hooks/useUnsavedChangesProcessing'
import { useGetGPs, useUpdateGPMutation, type UpdateGPBody } from '@/services/gp'
import { borderRadius, colors } from '@/theme'
import type { GP } from '@/types/gp'
import { zodResolver } from '@hookform/resolvers/zod'
import { Edit as EditIcon } from '@mui/icons-material'
import { Box, Button, IconButton, Paper, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

const detailsSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address').or(z.literal('')),
  contact_number: z.string()
})

type DetailsFormData = z.infer<typeof detailsSchema>

const addressSchema = z.object({
  address_line_1: z.string().min(1, 'Address line 1 is required'),
  address_line_2: z.string(),
  address_line_3: z.string(),
  address_line_4: z.string(),
  county: z.string().min(1, 'County is required'),
  postcode: z.string().min(1, 'Post code is required')
})

type AddressFormData = z.infer<typeof addressSchema>

const sectionStyles = {
  border: `1px solid ${colors.border.default}`,
  borderRadius: borderRadius.lg,
  overflow: 'hidden'
}

const gridRowStyles = (isEditMode: boolean) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 3,
  '& > *:not(:nth-child(4n))': {
    borderRight: isEditMode ? 'none' : `1px solid ${colors.border.default}`,
    pr: isEditMode ? 0 : 3
  }
})

function getDetailsDefaults(gp: GP): DetailsFormData {
  return {
    name: gp.name ?? '',
    email: gp.email ?? '',
    contact_number: gp.contact_number ?? ''
  }
}

function getAddressDefaults(gp: GP): AddressFormData {
  return {
    address_line_1: gp.address_line_1 ?? '',
    address_line_2: gp.address_line_2 ?? '',
    address_line_3: gp.address_line_3 ?? '',
    address_line_4: gp.address_line_4 ?? '',
    county: gp.county ?? '',
    postcode: gp.postcode ?? ''
  }
}

function pickDirty<T extends Record<string, unknown>>(
  data: T,
  dirtyFields: Partial<Record<keyof T, boolean>>
): Partial<T> {
  const result: Partial<T> = {}
  for (const key of Object.keys(dirtyFields) as (keyof T)[]) {
    if (dirtyFields[key]) result[key] = data[key]
  }
  return result
}

export default function GPDetailPage() {
  const { hash } = useParams<{ hash: string }>()
  const { data: gps } = useGetGPs()
  const updateMutation = useUpdateGPMutation(hash ?? '')
  const readOnly = !canUpdateGPs()

  const gp = hash ? gps?.find((g: GP) => g.hash === hash) : undefined

  const detailsDefaults = gp ? getDetailsDefaults(gp) : { name: '', email: '', contact_number: '' }
  const detailsForm = useForm<DetailsFormData>({
    resolver: zodResolver(detailsSchema),
    defaultValues: detailsDefaults
  })

  const addressDefaults = gp
    ? getAddressDefaults(gp)
    : { address_line_1: '', address_line_2: '', address_line_3: '', address_line_4: '', county: '', postcode: '' }
  const addressForm = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: addressDefaults
  })

  const [isDetailsEditMode, setIsDetailsEditMode] = useState(false)
  const [isAddressEditMode, setIsAddressEditMode] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync defaults on gp change
  useEffect(() => {
    if (gp) detailsForm.reset(getDetailsDefaults(gp))
  }, [gp?.name, gp?.email, gp?.contact_number])

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync defaults on gp change
  useEffect(() => {
    if (gp) addressForm.reset(getAddressDefaults(gp))
  }, [gp?.address_line_1, gp?.address_line_2, gp?.address_line_3, gp?.address_line_4, gp?.county, gp?.postcode])

  useUnsavedChangesProcessing(
    (isDetailsEditMode && detailsForm.formState.isDirty) || (isAddressEditMode && addressForm.formState.isDirty)
  )

  if (!gp) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>GP not found</Typography>
      </Box>
    )
  }

  const isSaving = updateMutation.isPending

  const handleDetailsEdit = () => {
    detailsForm.reset(getDetailsDefaults(gp))
    setIsDetailsEditMode(true)
  }

  const handleDetailsCancel = () => {
    setIsDetailsEditMode(false)
    detailsForm.reset(getDetailsDefaults(gp))
  }

  const onDetailsSubmit = (data: DetailsFormData) => {
    if (!canUpdateGPs()) return
    const changes = pickDirty(data, detailsForm.formState.dirtyFields) as UpdateGPBody
    if (Object.keys(changes).length === 0) {
      setIsDetailsEditMode(false)
      return
    }
    updateMutation.mutate(changes, {
      onSuccess: () => {
        enqueueSnackbar('GP details updated successfully', { variant: 'success' })
        setIsDetailsEditMode(false)
      }
    })
  }

  const handleAddressEdit = () => {
    addressForm.reset(getAddressDefaults(gp))
    setIsAddressEditMode(true)
  }

  const handleAddressCancel = () => {
    setIsAddressEditMode(false)
    addressForm.reset(getAddressDefaults(gp))
  }

  const onAddressSubmit = (data: AddressFormData) => {
    if (!canUpdateGPs()) return
    const changes = pickDirty(data, addressForm.formState.dirtyFields) as UpdateGPBody
    if (Object.keys(changes).length === 0) {
      setIsAddressEditMode(false)
      return
    }
    updateMutation.mutate(changes, {
      onSuccess: () => {
        enqueueSnackbar('GP address updated successfully', { variant: 'success' })
        setIsAddressEditMode(false)
      }
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
      <BackButton fallbackPath='/gps' defaultLabel='Back to GPs list' />

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='pageTitle' sx={{ fontSize: '24px' }}>
          {gp.name}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {/* GP Details Section */}
        <Paper elevation={1} sx={sectionStyles} component='form' onSubmit={detailsForm.handleSubmit(onDetailsSubmit)}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3, pt: 3 }}>
            <Typography variant='sectionTitle'>GP Details</Typography>
            {!isDetailsEditMode && !readOnly && (
              <IconButton onClick={handleDetailsEdit} size='small' color='primary' type='button'>
                <EditIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            )}
            {isDetailsEditMode && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant='primary' type='submit' disabled={!detailsForm.formState.isDirty || isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant='ghost' onClick={handleDetailsCancel} type='button' disabled={isSaving}>
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 3, pb: 3 }}>
            <Box sx={gridRowStyles(isDetailsEditMode)}>
              <FormField
                name='name'
                label='Name'
                control={detailsForm.control}
                isEditMode={isDetailsEditMode}
                error={detailsForm.formState.errors.name?.message}
              />
              <FormField
                name='email'
                label='Email'
                control={detailsForm.control}
                isEditMode={isDetailsEditMode}
                error={detailsForm.formState.errors.email?.message}
              />
              <FormField
                name='contact_number'
                label='Mobile'
                control={detailsForm.control}
                isEditMode={isDetailsEditMode}
                error={detailsForm.formState.errors.contact_number?.message}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant='fieldLabel' sx={{ mb: 1 }}>
                  NHS ID
                </Typography>
                <Typography variant='fieldValue'>{gp.nhs_id || '-'}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Address Section */}
        <Paper elevation={1} sx={sectionStyles} component='form' onSubmit={addressForm.handleSubmit(onAddressSubmit)}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3, pt: 3 }}>
            <Typography variant='sectionTitle'>Address</Typography>
            {!isAddressEditMode && !readOnly && (
              <IconButton onClick={handleAddressEdit} size='small' color='primary' type='button'>
                <EditIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            )}
            {isAddressEditMode && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant='primary' type='submit' disabled={!addressForm.formState.isDirty || isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant='ghost' onClick={handleAddressCancel} type='button' disabled={isSaving}>
                  Cancel
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, px: 3, pb: 3 }}>
            <Box sx={gridRowStyles(isAddressEditMode)}>
              <FormField
                name='address_line_1'
                label='Address Line 1'
                control={addressForm.control}
                isEditMode={isAddressEditMode}
                error={addressForm.formState.errors.address_line_1?.message}
              />
              <FormField
                name='address_line_2'
                label='Address Line 2'
                control={addressForm.control}
                isEditMode={isAddressEditMode}
                error={addressForm.formState.errors.address_line_2?.message}
              />
              <FormField
                name='address_line_3'
                label='Address Line 3'
                control={addressForm.control}
                isEditMode={isAddressEditMode}
                error={addressForm.formState.errors.address_line_3?.message}
              />
              <FormField
                name='address_line_4'
                label='Address Line 4'
                control={addressForm.control}
                isEditMode={isAddressEditMode}
                error={addressForm.formState.errors.address_line_4?.message}
              />
            </Box>

            <Box sx={gridRowStyles(isAddressEditMode)}>
              <FormField
                name='county'
                label='County'
                control={addressForm.control}
                isEditMode={isAddressEditMode}
                error={addressForm.formState.errors.county?.message}
              />
              <FormField
                name='postcode'
                label='Post Code'
                control={addressForm.control}
                isEditMode={isAddressEditMode}
                error={addressForm.formState.errors.postcode?.message}
              />
              <Box />
              <Box />
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}
